import React from 'react';
import { Card, CardContent, Typography, Avatar, Box } from '@mui/material';

interface MeetingCardProps {
  icon?: React.ReactElement;
  image?: string;
  name: string;
  title: string;
  isSpeaking?: boolean;
  imagePosition?: 'top' | 'bottom';
}

const avatarStyles = {
  width: 80,
  height: 80,
  transition: 'box-shadow 0.2s, background 0.2s',
};

const imageBoxSize = 112; // Size of image box, px

const MeetingCard: React.FC<MeetingCardProps> = ({
  name,
  title,
  icon,
  image,
  isSpeaking,
  imagePosition = 'top',
}) => {
  const showImage = Boolean(image);

  return (
    <Card
      sx={{
        width: '100%',
        aspectRatio: '1 / 1',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
        boxShadow: isSpeaking ? 8 : 1,
        border: isSpeaking ? '2px solid #7D5FFF' : 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <CardContent
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {/* Square image box at top */}
        {showImage && imagePosition === 'top' && (
          <Box
            sx={{
              aspectRatio: '1/1',
              mb: 2,
              mx: 'auto',
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: isSpeaking
                ? '0 0 0 6px #7D5FFF99, 0 0 10px 2px #7D5FFF55'
                : 1,
              border: isSpeaking ? '2px solid #7D5FFF' : '1px solid #e0e0e0',
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={image}
              alt={name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top left',
                display: 'block',
                borderRadius: '50%',
              }}
            />
          </Box>
        )}

        {/* Avatar fallback */}
        {!showImage && (
          <Box
            sx={{
              width: avatarStyles.width,
              height: avatarStyles.height,
              mb: 2,
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Avatar
              sx={{
                ...avatarStyles,
                bgcolor: isSpeaking ? 'primary.main' : 'grey.400',
                boxShadow: isSpeaking
                  ? '0 0 0 6px #7D5FFF99, 0 0 10px 2px #7D5FFF55'
                  : undefined,
              }}
            >
              {icon}
            </Avatar>
          </Box>
        )}

        {/* Name & Title */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 1,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ mb: 0.5, textAlign: 'center' }}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {title}
          </Typography>
        </Box>

        {/* Optionally: Square image at bottom */}
        {showImage && imagePosition === 'bottom' && (
          <Box
            sx={{
              width: imageBoxSize,
              height: imageBoxSize,
              mt: 2,
              mx: 'auto',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: isSpeaking
                ? '0 0 0 6px #7D5FFF99, 0 0 10px 2px #7D5FFF55'
                : 1,
              border: isSpeaking ? '2px solid #7D5FFF' : '1px solid #e0e0e0',
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={image}
              alt={name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MeetingCard;